<div class="w-full flex items-center justify-between h-10">
  <nav class="flex items-center gap-1">
    <!-- Home -->
    @if (parentRoute) {
    <a [routerLink]="['', 'module-selection']"
      class="flex items-center justify-center text-slate-400 hover:text-[#0e3b2e] transition-colors duration-200"
      title="Home">
      <mat-icon class="!text-lg !w-5 !h-5 flex items-center justify-center">home</mat-icon>
    </a>
    }

    <!-- Items -->
    @for (item of breadcrumbs; track $index; let last = $last) {
    <!-- Separator -->
    <div class="flex items-center justify-center px-1 text-slate-400">
      <mat-icon class="!text-base !w-4 !h-4 flex items-center justify-center">chevron_right</mat-icon>
    </div>
    <!-- Link -->
    @if (!last && (item.url || item.command)) {
    <a [routerLink]="item.url" (click)="!item.url && item.command?.(item)"
      class="text-xs font-bold text-slate-700 hover:text-[#0e3b2e] transition-colors duration-200 flex items-center leading-none">
      {{ item.label }}
    </a>
    }
    <!-- Active/Last -->
    @if (last) {
    <span class="text-xs font-bold text-[#e6b012] flex items-center leading-none">
      {{ item.label }}
    </span>
    }
    }
  </nav>

  <!-- Right Side Actions (Notification, Fullscreen, Profile) -->
  <div class="actions-section flex items-center gap-2 relative z-[99999]">
    <!-- Fullscreen Toggle -->
    <button type="button"
      class="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-600 hover:text-[#0e3b2e] hover:bg-slate-100/80 transition-colors cursor-pointer border-0 bg-transparent outline-none"
      (click)="toggleFullscreen()" [matTooltip]="isFullscreen() ? 'Exit Fullscreen' : 'Fullscreen'"
      matTooltipClass="white-tooltip">
      <mat-icon class="!text-lg !w-5 !h-5 flex items-center justify-center">{{ isFullscreen() ? 'fullscreen_exit' :
        'crop_free' }}</mat-icon>
    </button>

    <!-- Notifications -->
    <div class="action-wrapper inline-flex items-center relative">
      <button type="button"
        class="relative h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-600 hover:text-[#0e3b2e] hover:bg-slate-100/80 transition-colors cursor-pointer border-0 bg-transparent outline-none"
        [class.bg-slate-100]="isNotificationOpen()" (click)="toggleNotifications($event)" [matBadge]="unreadCount()"
        matBadgeColor="warn" matBadgeSize="small" [matBadgeHidden]="unreadCount() === 0">
        <mat-icon class="!text-lg !w-5 !h-5 flex items-center justify-center">notifications</mat-icon>
      </button>

      @if (isNotificationOpen()) {
      <div
        class="absolute right-0 top-[calc(100%+0.75rem)] bg-[var(--surface)] w-[360px] max-sm:w-auto rounded-xl shadow-lg border border-[var(--border)] overflow-hidden z-[99999] animate-[dropdownEnter_0.2s_cubic-bezier(0.16,1,0.3,1)] sm:absolute max-sm:fixed max-sm:left-4 max-sm:right-4 max-sm:top-[4.5rem]"
        (clickOutside)="isNotificationOpen.set(false)">
        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]">
          <h3 class="text-sm font-semibold text-primary">Notifications</h3>
          <div class="flex gap-2 items-center">
            <span class="bg-slate-100 text-slate-600 text-xs font-bold px-2 py-0.5 rounded-full">
              {{ notifications().length }} Available
            </span>
            <span class="bg-primary text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {{ unreadCount() }} New
            </span>
          </div>
        </div>
        <!-- List -->
        <div class="flex flex-col max-h-80 overflow-y-auto">
          @for (notification of notifications(); track notification.id) {
          <div
            class="flex items-start gap-3 px-4 py-3 text-sm cursor-pointer relative hover:bg-[var(--primary-subtle)] transition-all duration-150"
            [class.font-semibold]="!notification.read" (click)="handleNotificationClick(notification)">
            <!-- Icon -->
            <div class="flex-shrink-0 w-4 h-4 text-[var(--primary)] flex items-center justify-center">
              <mat-icon class="!text-base !w-4 !h-4 flex items-center justify-center">{{ notification.icon }}</mat-icon>
            </div>

            <!-- Content -->
            <div class="flex-1">
              <p class="text-[var(--text-primary)]">{{ notification.title }}</p>
              <p class="text-[var(--text-muted)] text-xs">{{ notification.message }}</p>
              <span class="text-[var(--text-muted)] text-[0.65rem]">{{
                notification.time
                }}</span>
            </div>
            @if (!notification.read) {
            <div class="absolute top-3 right-3 w-2 h-2 bg-primary rounded-full"></div>
            }
          </div>
          } @empty {
          <div class="flex flex-col items-center justify-center py-6 text-[var(--text-muted)]">
            <mat-icon class="text-2xl mb-2 flex items-center justify-center">notifications_off</mat-icon>
            <p class="text-sm">No notifications</p>
          </div>
          }
        </div>

        <!-- Footer -->
        <div class="px-4 py-2 border-t border-[var(--border)]">
          <button (click)="markAllAsRead($event)"
            class="w-full text-primary text-sm font-medium hover:bg-[var(--primary-subtle)] transition-colors rounded-md px-2 py-1">
            Mark All as Read
          </button>
        </div>
      </div>
      }
    </div>

    <!-- Language Selector Pill -->
    <div
      class="inline-flex items-center justify-center gap-0.5 text-xs font-extrabold text-slate-700 cursor-pointer hover:text-[#0e3b2e] hover:bg-slate-100/80 px-2.5 h-8 rounded-lg transition-colors select-none">
      <span class="leading-none flex items-center">EN</span>
      <mat-icon
        class="!text-base !w-4 !h-4 flex items-center justify-center text-slate-500 shrink-0">expand_more</mat-icon>
    </div>

    <!-- Profile Dropdown Button (Dark Green Block) -->
    <div class="action-wrapper flex items-center -mr-8 relative z-[99999]" clickOutside (clickOutside)="isProfileOpen.set(false)">
      <button (click)="toggleProfile($event)"
        class="bg-[#0e3b2e] hover:bg-[#07231a] text-white px-5 h-14 flex items-center gap-3 shadow-md cursor-pointer transition-colors border-l border-[#e6b012]/30">
        <div
          class="relative w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-bold shrink-0">
          @if (user().avatarUrl) {
          <img [src]="user().avatarUrl" [alt]="user().name" class="w-full h-full rounded-full object-cover" />
          } @else {
          <span>{{ getInitials(user().name || 'Agripina Mvungi') }}</span>
          }
          <span class="absolute bottom-0 right-0 w-2 h-2 rounded-full bg-emerald-400 border border-[#0e3b2e]"></span>
        </div>

        <span class="text-xs font-extrabold text-white tracking-tight whitespace-nowrap flex items-center leading-none">
          {{ user().name || 'Agripina Mvungi' }}
        </span>

        <mat-icon class="!text-base !w-4 !h-4 text-white/80 transition-transform flex items-center justify-center"
          [class.rotate-180]="isProfileOpen()">keyboard_arrow_down</mat-icon>
      </button>

      @if (isProfileOpen()) {
      <div class="
  absolute right-0 top-[calc(100%+0.75rem)]
  bg-[var(--surface)] min-w-[280px] rounded-xl shadow-lg border border-[var(--border)]
  overflow-hidden z-[99999]
  animate-[dropdownEnter_0.2s_cubic-bezier(0.16,1,0.3,1)]
  {{ leaving() ? 'animate-dropdown-leave' : '' }}
  sm:absolute max-sm:fixed max-sm:left-4 max-sm:right-4 max-sm:top-[4.5rem]
">
        <div class="flex flex-col py-2">
          <!-- Profile -->
          <a routerLink="/profile"
            class="flex items-center cursor-pointer gap-3 px-4 py-3 text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--primary-subtle)] hover:text-primary transition-all duration-150 hover:pl-6">
            <mat-icon class="text-lg text-primary">person_outline</mat-icon>
            <span>My Profile</span>
          </a>

          <!-- Security -->
          <a routerLink="/change-password"
            class="flex items-center cursor-pointer gap-3 px-4 py-3 text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--primary-subtle)] hover:text-primary transition-all duration-150 hover:pl-6">
            <mat-icon class="text-lg text-primary">lock</mat-icon>
            <span>Change Password</span>
          </a>

          <!-- Settings -->
          <a routerLink="/settings"
            class="flex items-center cursor-pointer gap-3 px-4 py-3 text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--primary-subtle)] hover:text-primary transition-all duration-150 hover:pl-6">
            <mat-icon class="text-lg text-primary">settings</mat-icon>
            <span>Settings</span>
          </a>

          <!-- Documents -->
          <a routerLink="/documents"
            class="flex items-center cursor-pointer gap-3 px-4 py-3 text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--primary-subtle)] hover:text-primary transition-all duration-150 hover:pl-6">
            <mat-icon class="text-lg text-primary">folder_open</mat-icon>
            <span>My Documents</span>
          </a>

          <!-- Notifications History -->
          <a routerLink="/notifications"
            class="flex items-center cursor-pointer gap-3 px-4 py-3 text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--primary-subtle)] hover:text-primary transition-all duration-150 hover:pl-6">
            <mat-icon class="text-lg text-primary">history</mat-icon>
            <span>Notification History</span>
          </a>

          <!-- Divider -->
          <div class="h-px bg-[var(--border)] my-2"></div>

          <!-- Logout -->
          <button (click)="logout()"
            class="flex items-center gap-3 px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 hover:text-red-700 transition-all duration-150 hover:pl-6 w-full text-left">
            <mat-icon class="text-lg text-red-600">logout</mat-icon>
            <span>Logout</span>
          </button>
        </div>
      </div>
      }
    </div>
  </div>
</div>